/* L'Orchidea - header: barra promo chiudibile, mega menu, ricerca che si allarga con risultati dal vivo,
   menu del telefono. */
class OrcHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this.parentElement;
    this.bar = this.querySelector('.orc-hd__bar');
    this.overlay = this.querySelector('[data-orc-overlay]');
    this.search = this.querySelector('[data-orc-search]');
    this.form = this.querySelector('[data-orc-search-form]');
    this.input = this.querySelector('[data-orc-search-input]');
    this.results = this.querySelector('[data-orc-search-results]');
    this.drawer = this.querySelector('[data-orc-drawer]');
    this.openTimer = 0;
    this.closeTimer = 0;
    this.current = null;
    this.hintHtml = this.results ? this.results.innerHTML : '';

    this.bindPromo();
    this.bindMega();
    this.bindSearch();
    this.bindDrawer();

    this.overlay?.addEventListener('click', () => this.closeAll());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll(true);
    });
  }

  /* ---------- Barra promozionale ---------- */
  bindPromo() {
    const promo = this.querySelector('[data-orc-promo]');
    const key = `orc-promo-closed:${this.dataset.promoKey || ''}`;
    const setHeight = () => {
      const h = promo && !promo.hidden ? promo.offsetHeight : 0;
      this.section.style.setProperty('--orc-promo-h', `${h}px`);
    };
    if (promo) {
      let closed = false;
      try { closed = localStorage.getItem(key) === '1'; } catch (e) { /* storage non disponibile */ }
      if (closed) promo.hidden = true;
      promo.querySelector('[data-orc-promo-close]')?.addEventListener('click', () => {
        promo.hidden = true;
        try { localStorage.setItem(key, '1'); } catch (e) { /* storage non disponibile */ }
        setHeight();
      });
    }
    setHeight();
    window.addEventListener('resize', setHeight);
  }

  // dove finisce la barra: serve a mettere velo e pannelli subito sotto
  measure() {
    const bottom = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
    this.style.setProperty('--orc-hd-bottom', `${bottom}px`);
  }

  /* ---------- Mega menu ---------- */
  bindMega() {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.querySelectorAll('[data-orc-item]').forEach((item) => {
      const id = item.dataset.orcItem;
      const panel = this.querySelector(`[data-orc-mega="${id}"]`);
      const link = item.querySelector('a');

      item.addEventListener('mouseenter', () => {
        if (!fine.matches) return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        // se un pannello è già aperto si passa subito all'altro, altrimenti piccolo ritardo
        this.openTimer = setTimeout(() => (panel ? this.openMega(id) : this.closeMega()), this.current ? 0 : 120);
      });
      link.addEventListener('focus', () => (panel ? this.openMega(id) : this.closeMega()));
      // touch su schermi larghi: primo tocco apre, secondo tocco segue il link
      link.addEventListener('click', (event) => {
        if (panel && !fine.matches && this.current !== id) {
          event.preventDefault();
          this.openMega(id);
        }
      });
    });

    this.addEventListener('mouseleave', () => {
      clearTimeout(this.openTimer);
      if (this.current) this.closeTimer = setTimeout(() => this.closeMega(), 180);
    });
    this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    this.querySelectorAll('[data-orc-mega]').forEach((panel) => {
      panel.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.closeMega();
      });
    });
  }

  openMega(id) {
    if (this.current === id) return;
    this.closeSearch();
    this.measure();
    this.querySelectorAll('[data-orc-mega]').forEach((panel) => {
      panel.hidden = panel.dataset.orcMega !== id;
    });
    this.querySelectorAll('[data-orc-item] > a[aria-controls]').forEach((a) => {
      a.setAttribute('aria-expanded', a.closest('[data-orc-item]').dataset.orcItem === id ? 'true' : 'false');
    });
    this.current = id;
    this.toggleOverlay(true);
  }

  closeMega() {
    if (!this.current) return;
    this.querySelectorAll('[data-orc-mega]').forEach((panel) => { panel.hidden = true; });
    this.querySelectorAll('[data-orc-item] > a[aria-controls]').forEach((a) => a.setAttribute('aria-expanded', 'false'));
    this.current = null;
    if (!this.searchOpen) this.toggleOverlay(false);
  }

  /* ---------- Ricerca ---------- */
  bindSearch() {
    if (!this.search || !this.input) return;
    this.input.addEventListener('focus', () => this.openSearch());
    this.input.addEventListener('click', () => this.openSearch());
    this.querySelector('[data-orc-search-close]')?.addEventListener('click', () => {
      this.input.value = '';
      this.results.innerHTML = this.hintHtml;
      this.closeSearch();
    });
    this.querySelectorAll('[data-orc-search-term]').forEach((chip) => {
      chip.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.value = chip.dataset.orcSearchTerm;
        this.input.focus();
        this.suggest();
      });
    });
    let timer = 0;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.suggest(), 220);
    });
    this.form?.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
  }

  openSearch() {
    if (this.searchOpen) return;
    this.closeMega();
    this.searchOpen = true;
    this.classList.add('is-search-open');
    this.input.setAttribute('aria-expanded', 'true');
    // il pannello si apre quando il campo ha finito di allargarsi
    this.measure();
    this.search.hidden = false;
    this.toggleOverlay(true);
    if (window.matchMedia('(max-width: 989px)').matches) document.body.classList.add('orc-lock');
  }

  closeSearch(returnFocus) {
    if (!this.searchOpen) return;
    this.searchOpen = false;
    this.classList.remove('is-search-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.search.hidden = true;
    document.body.classList.remove('orc-lock');
    if (!this.current) this.toggleOverlay(false);
    if (returnFocus) this.input.blur();
  }

  async suggest() {
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.results.innerHTML = this.hintHtml;
      return;
    }
    const base = this.results.dataset.suggestUrl || '/search/suggest';
    const url = `${base}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=8&resources[options][unavailable_products]=last`;
    this.lastQuery = q;
    try {
      const response = await fetch(url);
      const data = await response.json();
      if (this.lastQuery !== q) return;
      this.renderResults(q, data.resources?.results || {});
    } catch (e) {
      this.results.innerHTML = '<p class="orc-search__hint">La ricerca non risponde. Premi Invio per cercare.</p>';
    }
  }

  renderResults(q, res) {
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const currency = window.Shopify?.currency?.active || 'EUR';
    const money = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n.toLocaleString('it-IT', { style: 'currency', currency }) : esc(v);
    };
    const searchUrl = `${this.results.dataset.searchUrl || '/search'}?q=${encodeURIComponent(q)}&type=product`;
    const products = res.products || [];
    const collections = res.collections || [];

    if (!products.length && !collections.length) {
      this.results.innerHTML = `<p class="orc-search__hint">Nessun risultato per “${esc(q)}”. Prova con un'altra parola o guarda le categorie.</p>`;
      return;
    }
    const colls = collections.length
      ? `<p class="orc-search__label">Categorie</p><ul class="orc-search__colls">${collections.slice(0, 6).map((c) => `<li><a href="${esc(c.url)}">${esc(c.title)}</a></li>`).join('')}</ul>`
      : '';
    const prods = products.length
      ? `<p class="orc-search__label">Prodotti</p><ul class="orc-search__grid">${products.map((p) => {
          const img = p.featured_image?.url || p.image;
          return `<li><a class="orc-search__product" href="${esc(p.url)}">
            <span class="orc-search__product-img">${img ? `<img src="${esc(img)}${img.includes('?') ? '&' : '?'}width=360" alt="" loading="lazy">` : ''}</span>
            ${p.vendor ? `<span class="orc-search__product-vendor">${esc(p.vendor)}</span>` : ''}
            <span class="orc-search__product-title">${esc(p.title)}</span>
            <span class="orc-search__product-price">${money(p.price)}</span>
          </a></li>`;
        }).join('')}</ul>`
      : '';
    this.results.innerHTML = `${colls}${prods}<a class="orc-search__all" href="${searchUrl}">Vedi tutti i risultati</a>`;
  }

  /* ---------- Menu del telefono ---------- */
  bindDrawer() {
    if (!this.drawer) return;
    const opener = this.querySelector('[data-orc-drawer-open]');
    opener?.addEventListener('click', () => {
      this.closeAll();
      this.drawer.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('orc-lock');
      this.drawer.querySelector('[data-orc-drawer-close]')?.focus();
    });
    const close = () => {
      if (this.drawer.hidden) return;
      this.drawer.hidden = true;
      opener?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('orc-lock');
      opener?.focus();
    };
    this.drawer.querySelector('[data-orc-drawer-close]')?.addEventListener('click', close);
    this.drawer.addEventListener('click', (event) => { if (event.target === this.drawer) close(); });
    this.closeDrawer = close;
  }

  /* ---------- Comune ---------- */
  toggleOverlay(show) {
    if (this.overlay) this.overlay.hidden = !show;
  }

  closeAll(returnFocus) {
    this.closeMega();
    this.closeSearch(returnFocus);
    this.closeDrawer?.();
  }
}

if (!customElements.get('orc-header')) customElements.define('orc-header', OrcHeader);
